
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { NavItemType } from "./navItems";

export interface NavItemProps extends NavItemType {
  isActive: boolean;
  closeSidebar: () => void;
  toggleAccordionItem: (itemTitle: string) => void;
  showLabels?: boolean;
}

export const NavItem = ({ 
  title, 
  href, 
  icon: Icon, 
  color, 
  children, 
  isActive,
  closeSidebar,
  toggleAccordionItem,
  showLabels = true
}: NavItemProps) => {
  if (children && children.length > 0) {
    return (
      <AccordionItem value={title} key={title} className={!showLabels ? "border-b-0" : ""}>
        <AccordionTrigger
          className={`
            group flex items-center justify-between px-4 py-3 text-sm font-medium 
            transition-all duration-200 
            hover:bg-og01-card/50 
            ${!showLabels ? "justify-center" : ""} 
            ${isActive ? "text-og01-purple bg-og01-card/30" : "text-og01-text-secondary hover:text-og01-text"}
          `}
          onClick={() => toggleAccordionItem(title)}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`flex items-center gap-3 ${!showLabels ? "justify-center w-full" : ""}`}>
                  <Icon className={`h-5 w-5 transition-colors ${isActive ? "text-og01-purple" : "text-og01-text-muted group-hover:text-og01-text-secondary"}`} />
                  {showLabels && <span>{title}</span>}
                </div>
              </TooltipTrigger>
              {!showLabels && (
                <TooltipContent side="right" className="bg-og01-card border-og01-card text-og01-text">
                  {title}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </AccordionTrigger>
        {showLabels && (
          <AccordionContent className="space-y-1 bg-og01-bg-alt/50">
            {children.map((child) => (
              <NavSubItem 
                key={child.title} 
                {...child} 
                isActive={location.pathname === child.href} 
                closeSidebar={closeSidebar} 
                showLabels={showLabels}
              />
            ))}
          </AccordionContent>
        )}
      </AccordionItem>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={href}
            className={`
              flex items-center gap-3 px-4 py-3 text-sm font-medium 
              transition-all duration-200 
              ${isActive 
                ? "bg-og01-card/50 text-og01-text border-l-2 border-og01-purple" 
                : "text-og01-text-secondary hover:text-og01-text hover:bg-og01-card/30"
              } 
              ${!showLabels ? "justify-center" : ""}
            `}
            onClick={closeSidebar}
          >
            <Icon className={`h-5 w-5 transition-colors ${isActive ? "text-og01-purple" : "text-og01-text-muted"}`} />
            {showLabels && <span>{title}</span>}
          </Link>
        </TooltipTrigger>
        {!showLabels && (
          <TooltipContent side="right" className="bg-og01-card border-og01-card text-og01-text">
            {title}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

interface NavSubItemProps {
  title: string;
  href: string;
  icon: any;
  color: string;
  isActive: boolean;
  closeSidebar: () => void;
  showLabels?: boolean;
}

export const NavSubItem = ({ 
  title, 
  href, 
  icon: Icon, 
  color, 
  isActive,
  closeSidebar,
  showLabels = true
}: NavSubItemProps) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={href}
            className={`
              flex items-center gap-2 px-6 py-2 text-sm 
              transition-all duration-200 
              ${isActive 
                ? "text-og01-purple bg-og01-card/30 font-medium" 
                : "text-og01-text-muted hover:text-og01-text-secondary hover:bg-og01-card/20"
              } 
              ${!showLabels ? "justify-center" : ""}
            `}
            onClick={closeSidebar}
          >
            <Icon className={`h-4 w-4 ${isActive ? "text-og01-purple" : "text-og01-text-muted"}`} />
            {showLabels && <span>{title}</span>}
          </Link>
        </TooltipTrigger>
        {!showLabels && (
          <TooltipContent side="right" className="bg-og01-card border-og01-card text-og01-text">
            {title}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};
